import React from 'react';
import { SoccerCompetition } from '@/types';
import { Checkbox } from '@/components/ui/checkbox';
import { motion } from 'framer-motion';
import CompetitionIcon from './CompetitionIcons';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { supportedSoccerCompetitions } from '@/utils/sportUtils';

interface CompetitionCheckboxesProps {
  selectedCompetitions: SoccerCompetition[];
  onChange: (competitions: SoccerCompetition[]) => void;
  className?: string;
  availableCompetitions?: SoccerCompetition[];
  disabled?: boolean;
}

const CompetitionCheckboxes: React.FC<CompetitionCheckboxesProps> = ({ 
  selectedCompetitions, 
  onChange,
  className,
  availableCompetitions,
  disabled = false
}) => {
  const competitionValues = availableCompetitions !== undefined
    ? availableCompetitions
    : supportedSoccerCompetitions;

  const competitions = competitionValues.map(competition => ({
    value: competition,
    label: competition
  }));
  
  const handleCompetitionToggle = (competition: SoccerCompetition) => {
    if (selectedCompetitions.includes(competition)) {
      // Remove competition if already selected
      onChange(selectedCompetitions.filter(comp => comp !== competition));
    } else {
      // Add competition if not selected
      onChange([...selectedCompetitions, competition]);
    }
  };

  const allVisibleCompetitionsSelected = competitions.length > 0
    && competitions.every(comp => selectedCompetitions.includes(comp.value));

  const handleSelectAll = () => {
    if (allVisibleCompetitionsSelected) {
      // If all are selected, deselect all
      onChange([]);
    } else {
      // Otherwise, select all
      onChange(competitions.map(comp => comp.value));
    }
  };
  
  return (
    <div 
      className={`${className}`}
      data-custom-name="competition-checkboxes"
    >
      <Card className="p-3 mb-2 bg-gray-800 border-gray-700">
        <div className="flex flex-col space-y-2">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-sm font-medium text-gray-200">Competitions</h3>
            <button 
              onClick={handleSelectAll}
              className={`text-xs text-indigo-400 hover:text-indigo-300 transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={disabled || competitions.length === 0}
            >
              {allVisibleCompetitionsSelected ? 'Clear' : 'Select all'}
            </button>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {competitions.length === 0 ? (
              <p className="text-sm text-gray-400">No competitions in this feed.</p>
            ) : competitions.map((comp) => (
              <div key={comp.value} className={`flex items-center space-x-2 group ${disabled ? 'opacity-70' : ''}`}>
                <Checkbox 
                  id={`competition-${comp.value}`} 
                  checked={selectedCompetitions.includes(comp.value)}
                  onCheckedChange={() => handleCompetitionToggle(comp.value)}
                  disabled={disabled}
                  className={disabled ? 'cursor-not-allowed' : ''}
                />
                <Label 
                  htmlFor={`competition-${comp.value}`}
                  className={`flex items-center text-sm font-medium text-gray-400 group-hover:text-white transition-colors ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <CompetitionIcon competition={comp.value} className="w-4 h-4 mr-1.5" />
                  {comp.label}
                </Label>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default CompetitionCheckboxes;
